/**
 * Remote Player Class
 *
 * Extends shared Player with client-side interpolation for smooth rendering
 * Used for rendering other players (not the local player)
 */

import { Player } from '../shared/Player.js';
import { gameState } from './client.js';

function lerpAngle(start, end, t) {
  let delta = end - start;
  // Normalize delta to [-PI, PI] for shortest path
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  return start + delta * t;
}

// Maximum age of snapshots to keep (ms) - keep enough for interpolation + buffer
const MAX_SNAPSHOT_AGE = 1000;
// Maximum extrapolation time before snapping to last known state (ms)
const MAX_EXTRAPOLATION_TIME = 250;

/** Rewind snap-back position is private to the activating player. */
function stripPrivateRewindFields(effects) {
  if (!effects) return effects;
  const {
    rewindStartX: _x,
    rewindStartY: _y,
    rewindStartAngle: _a,
    rewindEndTime: _t,
    ...rest
  } = effects;
  return rest;
}

class RemotePlayer extends Player {
  constructor(config) {
    super(config);

    // Client-side rendering properties
    // `t` is the SERVER's timestamp for the snapshot, so interpolation runs on
    // the server clock and is immune to client-side receive jitter.
    this.serverHistory = []; // [{t, x, y, vx, vy, angle}]
  }

  /**
   * Update player from server snapshot (supports delta compression)
   * @param {Object} state - Server state snapshot (may be partial for delta updates)
   * @param {boolean} isKeyframe - True if this is a full state update
   * @param {number|null} serverTime - snapshot.serverTime; falls back to the local server-clock estimate
   */
  updateFromServer(state, isKeyframe = false, serverTime = null) {
    const snapshotTime = typeof serverTime === 'number' ? serverTime : gameState.serverNow();

    // For delta updates, merge with last known state
    // For keyframes, use full state directly
    const lastSnapshot = this.serverHistory.length > 0
      ? this.serverHistory[this.serverHistory.length - 1]
      : { x: this.x, y: this.y, vx: this.vx, vy: this.vy, angle: this.angle };

    // Build complete snapshot by merging delta with last known state
    const newSnapshot = {
      t: snapshotTime,
      x: state.x ?? lastSnapshot.x,
      y: state.y ?? lastSnapshot.y,
      vx: state.vx ?? lastSnapshot.vx,
      vy: state.vy ?? lastSnapshot.vy,
      angle: state.angle ?? lastSnapshot.angle
    };

    this.serverHistory.push(newSnapshot);

    // Prune old snapshots based on age rather than fixed count
    // Keep snapshots within MAX_SNAPSHOT_AGE for interpolation
    const cutoffTime = snapshotTime - MAX_SNAPSHOT_AGE;
    while (this.serverHistory.length > 2 && this.serverHistory[0].t < cutoffTime) {
      this.serverHistory.shift();
    }

    // Build merged state for base class update (handles health, weapon, cosmetics, ability effects)
    const mergedState = {
      x: newSnapshot.x,
      y: newSnapshot.y,
      vx: newSnapshot.vx,
      vy: newSnapshot.vy,
      angle: newSnapshot.angle,
      health: state.health ?? this.health,
      shieldHealth: state.shieldHealth ?? this.shieldHealth,
      maxShield: state.maxShield ?? this.maxShield,
      cosmetics: state.cosmetics ?? this.cosmetics,
      weapon: state.weapon ?? {
        currentAmmo: this.weaponState.currentAmmo,
        reloading: this.weaponState.reloading,
        reloadStartTime: this.weaponState.reloadStartTime,
        reloadTime: this.weaponState.reloadTime
      }
    };

    // Handle inventory and activeSlotIndex updates
    if ('inventory' in state) {
      mergedState.inventory = state.inventory;
    }
    if ('activeSlotIndex' in state) {
      mergedState.activeSlotIndex = state.activeSlotIndex;
    }
    if ('ammo' in state) {
      mergedState.ammo = state.ammo;
    }

    if ('stats' in state) {
      mergedState.stats = state.stats;
    }

    // Only include activeAbilityEffects if explicitly present in state
    // (delta compression omits unchanged fields - undefined means "no change", not "clear")
    if ('activeAbilityEffects' in state) {
      mergedState.activeAbilityEffects = stripPrivateRewindFields(state.activeAbilityEffects);
    }

    // Only include team if explicitly present in state
    // (delta compression omits unchanged fields - undefined means "no change", not "clear")
    if ('team' in state) {
      mergedState.team = state.team;
    }

    // Update game state using base class method
    this.updateFromState(mergedState);
  }

  /**
   * Find the two snapshots surrounding the target render time using binary search.
   * Returns { before, after } where before.t <= renderTime < after.t
   * Returns null if no valid interpolation pair exists.
   */
  findInterpolationSnapshots(renderTime) {
    const history = this.serverHistory;
    if (history.length < 2) return null;

    // Binary search for the first snapshot with t > renderTime
    let low = 0;
    let high = history.length;

    while (low < high) {
      const mid = (low + high) >>> 1;
      if (history[mid].t <= renderTime) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    // 'low' now points to first snapshot after renderTime
    // We need the snapshot before (low - 1) and after (low)
    if (low === 0 || low >= history.length) {
      return null; // renderTime is outside our snapshot range
    }

    return {
      before: history[low - 1],
      after: history[low]
    };
  }

  /**
   * Get the interpolated render state for this player.
   * @param {number} renderTime - The target time to render at (current time minus interpolation delay)
   * @returns {{ x: number, y: number, angle: number }}
   */
  getRenderState(renderTime) {
    // No history - return current state
    if (this.serverHistory.length === 0) {
      return { x: this.x, y: this.y, angle: this.angle };
    }

    const oldest = this.serverHistory[0];
    const newest = this.serverHistory[this.serverHistory.length - 1];

    // Case 1: renderTime is before our oldest snapshot - use oldest
    if (renderTime <= oldest.t) {
      return { x: oldest.x, y: oldest.y, angle: oldest.angle };
    }

    // Case 2: renderTime is within our snapshot range - interpolate
    const snapshots = this.findInterpolationSnapshots(renderTime);
    if (snapshots) {
      const { before, after } = snapshots;
      const dt = after.t - before.t;

      // Avoid division by zero
      if (dt <= 0) {
        return { x: after.x, y: after.y, angle: after.angle };
      }

      const t = (renderTime - before.t) / dt;
      // Clamp t to [0, 1] for safety
      const clampedT = Math.max(0, Math.min(1, t));

      return {
        x: before.x + (after.x - before.x) * clampedT,
        y: before.y + (after.y - before.y) * clampedT,
        angle: lerpAngle(before.angle, after.angle, clampedT)
      };
    }

    // Case 3: renderTime is after our newest snapshot - extrapolate or snap
    const timeSinceNewest = renderTime - newest.t;

    if (timeSinceNewest <= MAX_EXTRAPOLATION_TIME) {
      // Extrapolate using velocity (vx/vy are px/s from the shared Movement step)
      const extrapDt = timeSinceNewest / 1000; // Convert to seconds
      return {
        x: newest.x + newest.vx * extrapDt,
        y: newest.y + newest.vy * extrapDt,
        angle: newest.angle
      };
    }

    // Too long since last update - just use newest state (player may be lagging)
    return { x: newest.x, y: newest.y, angle: newest.angle };
  }
}

export { lerpAngle, MAX_SNAPSHOT_AGE, MAX_EXTRAPOLATION_TIME, RemotePlayer };
